import { useEffect, useState, useCallback } from 'react'
import leadService from '../../services/leadService'
import { HiOutlineTrash, HiOutlineSearch, HiOutlineX, HiOutlineEye } from 'react-icons/hi'

const fmt = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Ho_Chi_Minh',
    })
  } catch { return iso }
}

function DeletionLogManagement() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(null)
  const [detail, setDetail] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await leadService.deletionLogs({ search, page, per_page: 20 })
      if (res.status === 'success') { setLogs(res.data); setPagination(res.pagination) }
    } finally { setLoading(false) }
  }, [search, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [search])

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="page-title flex items-center gap-2"><HiOutlineTrash className="w-6 h-6 text-red-500" /> Lịch sử xóa hồ sơ</h1>
          <p className="page-subtitle">Truy vết mọi lần xóa ứng viên (ai xóa · khi nào · hồ sơ gì · lý do) để phát hiện gian lận.</p>
        </div>
        <div className="relative">
          <HiOutlineSearch className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
          <input className="input pl-10 w-64" placeholder="Tìm tên / SĐT / người xóa" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-secondary-400">Chưa có lịch sử xóa nào.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-2xs uppercase text-secondary-400 border-b border-secondary-100">
                  <th className="px-4 py-3">Thời gian xóa</th>
                  <th className="px-4 py-3">Người xóa</th>
                  <th className="px-4 py-3">Ứng viên</th>
                  <th className="px-4 py-3">SĐT</th>
                  <th className="px-4 py-3">Lý do</th>
                  <th className="px-4 py-3 text-right">Chi tiết</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-50">
                {logs.map((l) => (
                  <tr key={l.id} className="hover:bg-secondary-50/60">
                    <td className="px-4 py-3 whitespace-nowrap text-secondary-600">{fmt(l.deleted_at)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-50 text-red-700 px-2 py-0.5 rounded-full">
                        {l.deleted_by_name || `#${l.deleted_by || '?'}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-secondary-900">{l.full_name || '—'}</td>
                    <td className="px-4 py-3 text-secondary-600">{l.phone || '—'}</td>
                    <td className="px-4 py-3 text-secondary-500 max-w-[240px] truncate" title={l.reason || ''}>{l.reason || <span className="text-secondary-300">—</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setDetail(l)} className="text-primary-600 hover:text-primary-700" title="Xem hồ sơ đã xóa"><HiOutlineEye className="w-5 h-5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-secondary-100 text-sm">
            <span className="text-secondary-500">{pagination.total} lần xóa</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="btn-secondary btn-sm disabled:opacity-40">← Trước</button>
              <span className="text-secondary-500">{page}/{pagination.total_pages}</span>
              <button disabled={page >= pagination.total_pages} onClick={() => setPage((p) => p + 1)} className="btn-secondary btn-sm disabled:opacity-40">Sau →</button>
            </div>
          </div>
        )}
      </div>

      {detail && <SnapshotModal log={detail} onClose={() => setDetail(null)} />}
    </div>
  )
}

const FIELD_LABELS = {
  full_name: 'Họ tên', phone: 'SĐT', email: 'Email', birth_year: 'Năm sinh',
  current_location: 'Nơi ở', desired_area: 'KV mong muốn', region: 'Khu vực (raw)',
  area: 'Khu vực', position: 'Vị trí', experience: 'Kinh nghiệm', source: 'Nguồn',
  status: 'Trạng thái', note: 'Ghi chú', admin_note: 'Ghi chú nội bộ', created_at: 'Ngày tạo',
}

function SnapshotModal({ log, onClose }) {
  const s = log.snapshot || {}
  const rows = Object.entries(FIELD_LABELS).filter(([k]) => s[k] != null && s[k] !== '')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative card w-full max-w-lg p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">Hồ sơ đã xóa <span className="text-secondary-400 text-sm">#{log.lead_id}</span></h3>
          <button onClick={onClose} className="p-1.5 text-secondary-400 hover:text-secondary-700"><HiOutlineX className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-secondary-500 mb-3 bg-red-50 rounded-lg px-3 py-2">
          Xóa bởi <b>{log.deleted_by_name || '?'}</b> · {fmt(log.deleted_at)}
          {log.ip_address && <> · IP {log.ip_address}</>}
          {log.reason && <div className="mt-1">Lý do: <b>{log.reason}</b></div>}
        </div>
        <dl className="grid grid-cols-3 gap-y-2 text-sm">
          {rows.map(([k, label]) => (
            <div key={k} className="col-span-3 grid grid-cols-3 gap-2">
              <dt className="text-secondary-400">{FIELD_LABELS[k] || k}</dt>
              <dd className="col-span-2 text-secondary-800 break-words">{String(s[k])}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

export default DeletionLogManagement
